import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  ConfirmDialog,
  InlineAlert,
  LoadingState,
  PageHeader,
  Toast,
} from '../../components/admin/ui';

const uploadUrl = (path) => {
  if (!path) return null;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  return `${base}/uploads/${path}`;
};

const SAMPLE_MD = `# Tarifas Team Kitsune

## Mensualidades
- Principiante: **$X.XXX**
- Avanzado: **$X.XXX**

## Exámenes
Consultá fechas en Eventos.
`;

const isPublished = (data) =>
  Boolean(data && data.published !== false && (data.file_path || data.markdown_body));

const TariffsManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [mode, setMode] = useState('pdf');
  const [markdown, setMarkdown] = useState(SAMPLE_MD);
  const [pdfFile, setPdfFile] = useState(null);
  const [current, setCurrent] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });
  const [error, setError] = useState('');

  const fetchTariffs = useCallback(async () => {
    try {
      const res = await api.get('/admin/institutional/tariffs');
      const data = res.data.data;
      if (isPublished(data)) {
        setCurrent(data);
        if (data.content_type === 'markdown') {
          setMode('markdown');
          setMarkdown(data.markdown_body || SAMPLE_MD);
        } else {
          setMode('pdf');
        }
      } else {
        setCurrent(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setCurrent(null);
      } else {
        console.error(err);
        setError('No se pudo cargar el documento de tarifas');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (mode === 'pdf') {
        if (!pdfFile) {
          setError('Seleccioná un archivo PDF');
          setSaving(false);
          return;
        }
        const fd = new FormData();
        fd.append('content_type', 'pdf');
        fd.append('tariff_document', pdfFile);
        const res = await api.put('/admin/institutional/tariffs', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setCurrent(res.data.data);
        setPdfFile(null);
        setToast({ open: true, message: 'PDF de tarifas publicado', tone: 'success' });
      } else {
        const res = await api.put('/admin/institutional/tariffs', {
          content_type: 'markdown',
          markdown_body: markdown,
        });
        setCurrent(res.data.data);
        setToast({ open: true, message: 'Tarifas Markdown publicadas', tone: 'success' });
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Error al guardar tarifas');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    setError('');
    try {
      await api.delete('/admin/institutional/tariffs');
      setCurrent(null);
      setPdfFile(null);
      setMarkdown(SAMPLE_MD);
      setMode('pdf');
      setConfirmClear(false);
      setToast({
        open: true,
        message: 'Tarifas despublicadas. El sitio ya no muestra esta sección.',
        tone: 'success',
      });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'No se pudo despublicar');
    } finally {
      setClearing(false);
    }
  };

  if (loading) return <LoadingState />;

  const published = isPublished(current);
  const currentPdfUrl =
    published && current?.content_type === 'pdf' ? uploadUrl(current.file_path) : null;

  return (
    <div className="admin-page">
      <PageHeader title="Tarifas" onBack={() => navigate('/admin')} />
      <p className="mb-6 text-sm text-muted">
        Publicá las tarifas como PDF o Markdown. También podés despublicar y borrar el contenido
        actual; el sitio deja de mostrarlo hasta que vuelvas a publicar.
      </p>

      {published ? (
        <InlineAlert tone="success" className="mb-6">
          Publicado:{' '}
          <strong>{current.content_type === 'markdown' ? 'Markdown' : 'PDF'}</strong>
          {current.updated_at ? ` · actualizado ${current.updated_at}` : ''}
        </InlineAlert>
      ) : (
        <InlineAlert tone="info" className="mb-6">
          Sin publicar — la sección Tarifas no aparece en el sitio público.
        </InlineAlert>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <fieldset className="rounded-lg border border-line bg-surface p-5">
          <legend className="px-1 text-sm font-semibold">Formato de publicación</legend>
          <div className="mt-2 flex flex-wrap gap-4">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium">
              <input
                type="radio"
                name="content_type"
                value="pdf"
                checked={mode === 'pdf'}
                onChange={() => setMode('pdf')}
              />
              PDF
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium">
              <input
                type="radio"
                name="content_type"
                value="markdown"
                checked={mode === 'markdown'}
                onChange={() => setMode('markdown')}
              />
              Markdown
            </label>
          </div>
        </fieldset>

        {mode === 'pdf' ? (
          <div className="rounded-lg border border-line bg-surface p-5">
            <label htmlFor="tariff_pdf" className="mb-2 block text-sm font-semibold">
              Archivo PDF
            </label>
            <input
              id="tariff_pdf"
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            {pdfFile && (
              <p className="mt-2 text-sm text-muted">Seleccionado: {pdfFile.name}</p>
            )}
            {currentPdfUrl && !pdfFile && (
              <p className="mt-3 text-sm">
                PDF actual:{' '}
                <a
                  href={currentPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Ver / descargar
                </a>
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-line bg-surface p-5">
              <label htmlFor="markdown_body" className="mb-2 block text-sm font-semibold">
                Editor Markdown
              </label>
              <textarea
                id="markdown_body"
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                rows={18}
                className="w-full rounded-md border border-line bg-canvas px-3 py-2 font-mono text-sm text-ink"
                placeholder="# Tarifas..."
              />
            </div>
            <div className="rounded-lg border border-line bg-surface p-5">
              <p className="mb-2 text-sm font-semibold">Vista previa</p>
              <div className="tariffs-md max-h-[28rem] overflow-auto rounded-md border border-line bg-canvas p-4 text-sm">
                <ReactMarkdown>{markdown || '_Sin contenido_'}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <div className="flex flex-wrap gap-3">
          <button type="submit" className="btn-admin" disabled={saving || clearing}>
            {saving ? 'Publicando…' : published ? 'Actualizar publicación' : 'Publicar tarifas'}
          </button>
          {published && (
            <button
              type="button"
              className="btn-admin-danger"
              disabled={saving || clearing}
              onClick={() => setConfirmClear(true)}
            >
              Despublicar y borrar
            </button>
          )}
        </div>
      </form>

      <ConfirmDialog
        open={confirmClear}
        title="Despublicar tarifas"
        message="Se elimina el PDF o Markdown actual. En el sitio público deja de mostrarse la sección Tarifas hasta que vuelvas a publicar."
        confirmLabel="Despublicar y borrar"
        danger
        loading={clearing}
        onConfirm={handleClear}
        onClose={() => (!clearing ? setConfirmClear(false) : undefined)}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default TariffsManagement;
